'use client'

import clsx from 'clsx'
import { motion } from 'motion/react'
import React, { forwardRef } from 'react'
import type { ComponentPropsWithoutRef, ForwardedRef, ReactNode } from 'react'
import { TouchTarget } from './button'
import { Link } from '../shared/link'
import { themeClasses } from '../shared/themeClasses'

type NavbarItemBaseProps = {
  current?: boolean
  className?: string
  children?: ReactNode
  href?: string
}

type NavbarItemActionProps = Omit<ComponentPropsWithoutRef<'button'>, 'className' | 'children' | 'href'> &
  Omit<ComponentPropsWithoutRef<'a'>, 'className' | 'children' | 'href'>

type NavbarItemProps = NavbarItemBaseProps & NavbarItemActionProps

export const NavbarItem = forwardRef<HTMLElement, NavbarItemProps>(function NavbarItem(
  { current, className, children, href, ...props },
  ref
) {
  const classes = clsx(
    'relative flex min-w-0 items-center gap-3 rounded-lg p-2 text-left text-base/6 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 sm:text-sm/5',
    themeClasses.textPrimary,
    '*:data-[slot=icon]:size-6 *:data-[slot=icon]:shrink-0 sm:*:data-[slot=icon]:size-5',
    themeClasses.itemIconFillMuted,
    '*:not-nth-2:last:data-[slot=icon]:ml-auto *:not-nth-2:last:data-[slot=icon]:size-5 sm:*:not-nth-2:last:data-[slot=icon]:size-4',
    '*:data-[slot=avatar]:-m-0.5 *:data-[slot=avatar]:size-7 *:data-[slot=avatar]:[--avatar-radius:var(--radius-md)] sm:*:data-[slot=avatar]:size-6',
    'hover:bg-zinc-950/5 hover:*:data-[slot=icon]:fill-zinc-950 active:bg-zinc-950/10 dark:hover:bg-white/5 dark:hover:*:data-[slot=icon]:fill-white dark:active:bg-white/10'
  )

  return (
    <span className={clsx(className, 'relative')}>
      {current ? <motion.span layoutId="current-indicator" className={clsx('absolute inset-x-2 -bottom-2.5 h-0.5 rounded-full', themeClasses.indicatorStrong)} /> : null}
      {typeof href === 'string' ? (
        <Link
          {...(props as Omit<ComponentPropsWithoutRef<typeof Link>, 'className' | 'children' | 'href'>)}
          href={href}
          className={classes}
          data-current={current ? 'true' : undefined}
          ref={ref as ForwardedRef<HTMLAnchorElement>}
        >
          <TouchTarget>{children}</TouchTarget>
        </Link>
      ) : (
        <button
          {...(props as Omit<ComponentPropsWithoutRef<'button'>, 'className' | 'children'>)}
          type={(props as ComponentPropsWithoutRef<'button'>).type ?? 'button'}
          className={clsx('cursor-pointer', classes)}
          data-current={current ? 'true' : undefined}
          data-disabled={(props as ComponentPropsWithoutRef<'button'>).disabled ? 'true' : undefined}
          ref={ref as ForwardedRef<HTMLButtonElement>}
        >
          <TouchTarget>{children}</TouchTarget>
        </button>
      )}
    </span>
  )
})
